import React, { useContext, useEffect, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Card, notification, NOTIFICATION_TYPES, Popup, Table, Tooltip } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import type { TableColumnType, TableRow } from '_atoms/Table/types';
import { Context } from '_templates/AnalyticsDashboard/analytics-dashboard';
import { TableHeader } from '_molecules';
import { ReactComponent as Upload } from 'dist/icons/upload.svg';
import { ReactComponent as EmptyImage } from 'dist/images/common/icons/changelog.svg';
import { ReactComponent as CircularProgress } from 'dist/icons/loading.svg';
import { analyticWidgets } from 'actions/analyticActions';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import {
  hiringStatisticsColumnsMapper,
  hiringStatisticsDataMapper,
  successRateColumnsMapper,
  successRateDataMapper,
  useSyncScroll,
  type HiringStatisticsRow,
  type ReportingSectionResponse,
  type SuccessRateRow,
  type TransitionRow,
} from './utils';
import { COLUMNS, HIDE_PERCENTAGE, SECTION_URLS } from './constants';
import './style.scss';

interface HiringState {
  hiringData: HiringStatisticsRow[];
  successData: SuccessRateRow[];
  loading: boolean;
}

interface EmptyTableProps {
  loading?: boolean;
}

const EmptyTable = ({ loading }: EmptyTableProps) => {
  const { t } = useTranslation();
  return (
    <div className="empty-table">
      <EmptyImage className="empty-table__image" />
      <Typography.Title2 className="empty-table__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:analytics_reporting_info.text_no_data_found')}
      </Typography.Title2>
      <Typography.Title3 className="empty-table__subtitle" weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:analytics_reporting_info.text_try_another_criteria')}
      </Typography.Title3>
      {loading && (
        <div className="empty-table__loading">
          <CircularProgress width={40} height={40} />
        </div>
      )}
    </div>
  );
};

export const AnalyticsReportingInfo = () => {
  const { t } = useTranslation();
  const { appliedFilters } = useContext(Context)!;
  const [{ hiringData, successData, loading }, setHiringState] = useState<HiringState>({
    hiringData: [],
    successData: [],
    loading: false,
  });
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [hiringStatisticsColumns, setHiringStatisticsColumns] = useState<TableColumnType[]>([]);
  const [successRateColumns, setSuccessRateColumns] = useState<TableColumnType[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState(['0', '1', '2', '3', '4', '5', '6', '7']);

  useSyncScroll('.ant-table-body, .ant-table-content', [expandedRowKeys, hiringStatisticsColumns]);

  const exportExcel = () => {
    analyticWidgets('hiring-statistics/export/xlsx', appliedFilters, null).then(() => {
      setSuccessPopupOpen(true);
    });
  };

  const onExpand =
    ({ uuid }: { uuid: string }) =>
    () => {
      if (!expandedRowKeys.includes(uuid)) setExpandedRowKeys(rows => [...rows, uuid]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== uuid));
    };

  const onApplyAnalytics = () => {
    setHiringState(d => ({ ...d, loading: true }));

    const sectionRequests = SECTION_URLS.map(url => analyticWidgets(url, appliedFilters, null));

    Promise.all(sectionRequests)
      .then(resp => {
        const sections = resp as ReportingSectionResponse[];
        setHiringStatisticsColumns(hiringStatisticsColumnsMapper(sections[0].transitions[0].periods));
        setSuccessRateColumns(successRateColumnsMapper(sections[0].transitions[0].periods));
        setHiringState(d => ({
          ...d,
          loading: false,
          hiringData: hiringStatisticsDataMapper(sections),
          successData: successRateDataMapper(sections),
        }));
      })
      .catch(() => {
        setHiringStatisticsColumns([]);
        setSuccessRateColumns([]);
        setHiringState({ hiringData: [], loading: false } as HiringState);
        notification({
          message: t('translation:analytics_reporting_info.hiring_statistics_error'),
          btn: (
            <span role="none" onClick={onApplyAnalytics}>
              {t('translation:analytics_reporting_info.hiring_statistics_error_button')}
            </span>
          ),
          type: NOTIFICATION_TYPES.ERROR,
        });
      });
  };

  useEffect(onApplyAnalytics, [appliedFilters]);

  return (
    <>
      <div className="hiring-statistics-alert">
        <Info />
        {t('translation:analytics_reporting_info.alert')}
      </div>
      <Card className="hiring-statistics">
        <TableHeader
          title={t('translation:analytics_reporting_info.card_title_hiring_statistics')}
          extra={
            <Button icon={<Upload />} onClick={exportExcel}>
              {t('translation:analytics_reporting_info.button_export_excel')}
            </Button>
          }
        />
        <Table
          loading={loading}
          rowKey="uuid"
          rowClassName="bold-row"
          scroll={{ x: 'max-content' }}
          columns={[
            {
              ...COLUMNS[0],
              render: (name, record) => {
                const hiringRecord = record as HiringStatisticsRow;
                return hiringRecord.transitions && hiringRecord.transitions.length ? (
                  <div role="none" className="expanded-cell">
                    <div className="expanded-cell__info">
                      <Vector
                        className={cn('expanded-cell__icon', {
                          expanded: expandedRowKeys.includes(hiringRecord.uuid),
                        })}
                        onClick={onExpand(hiringRecord)}
                      />
                      <Typography.Text className="name">{name as string}</Typography.Text>
                    </div>
                  </div>
                ) : (
                  <div className="expanded-cell__info">{name as string}</div>
                );
              },
            },
            ...hiringStatisticsColumns,
          ]}
          data={hiringData}
          expandable={{
            expandedRowKeys,
            // Nested expandable tables match the previous JSX structure
            // eslint-disable-next-line react/no-unstable-nested-components
            expandedRowRender: ({ transitions }: HiringStatisticsRow) => (
              <Table
                rowKey="uuid"
                className="sub-table"
                scroll={{ x: 'max-content' }}
                rowClassName={record =>
                  HIDE_PERCENTAGE.includes((record as TransitionRow).name) ? 'disabled-row' : ''
                }
                columns={[
                  {
                    ...COLUMNS[0],
                    render: (name, record) => {
                      const transitionRecord = record as TransitionRow;
                      return transitionRecord.declined.length ? (
                        <div role="none" className="expanded-cell">
                          <div className="expanded-cell__info">
                            <Vector
                              className={cn('expanded-cell__icon', {
                                expanded: expandedRowKeys.includes(transitionRecord.uuid),
                              })}
                              onClick={onExpand(transitionRecord)}
                            />
                            {transitionRecord.tooltip ? (
                              <Tooltip label={transitionRecord.tooltip}>
                                <Typography.Text className="name">{name as string}</Typography.Text>
                              </Tooltip>
                            ) : (
                              <Typography.Text className="name">{name as string}</Typography.Text>
                            )}
                          </div>
                        </div>
                      ) : transitionRecord.tooltip ? (
                        <Tooltip label={transitionRecord.tooltip}>{name as string}</Tooltip>
                      ) : (
                        (name as string)
                      );
                    },
                  },
                  ...hiringStatisticsColumns,
                ]}
                data={(transitions || []) as TransitionRow[]}
                expandable={{
                  expandedRowKeys,
                  // eslint-disable-next-line react/no-unstable-nested-components
                  expandedRowRender: ({ declined }: TransitionRow) => (
                    <Table
                      rowKey="uuid"
                      className="sub-sub-table"
                      scroll={{ x: 'max-content' }}
                      rowClassName="grey-row"
                      columns={[COLUMNS[0], ...hiringStatisticsColumns]}
                      data={declined}
                      showCardHeader={false}
                      showCardFooter={false}
                    />
                  ),
                }}
                showCardHeader={false}
                showCardFooter={false}
              />
            ),
          }}
          showCardHeader={false}
          showCardFooter={false}
          emptyComponent={<EmptyTable />}
        />
      </Card>
      <Card className="success-rate">
        <TableHeader title={t('translation:analytics_reporting_info.card_title_success_rate')} />
        <Table
          rowKey="id"
          rowClassName={r => cn({ 'hiring-statistics__bold-row': !!(r as SuccessRateRow).title })}
          loading={loading}
          columns={successRateColumns}
          data={successData as TableRow[]}
          showCardHeader={false}
          showCardFooter={false}
          emptyComponent={<EmptyTable />}
        />
      </Card>
      <Popup
        open={successPopupOpen}
        title={t('translation:analytics_reporting_info.popup_title_success')}
        okButtonProps={{
          onClick: () => setSuccessPopupOpen(false),
          children: t('translation:analytics_reporting_info.button_ok'),
        }}
      >
        {t('translation:analytics_reporting_info.text_file_sent')}
      </Popup>
    </>
  );
};
